import { StyleSheet, css } from 'aphrodite';
import type { FC } from 'react';

import { Button } from '../../../web/components-v2/Button';
import { Toggle } from '../../../web/components/Toggle';

/**
 * Drawer-tab settings surface for the focused-view coaching pipeline. Mirrors the standard queue's
 * "Coaching Settings" panel.
 *
 * The bottom "Use focused view" toggle is preserved from that panel so a rep can switch back to the
 * standard queue (turning it off persists the opt-out and navigates to `/queue`); the "Return to
 * standard queue" link mirrors the same action for discoverability.
 *
 * Presentation only.
 */

export type SettingsPanelProps = {
	realtimeCoachingEnabled: boolean;
	autoSuggestionsEnabled: boolean;
	/** Whether the rep is currently opted into focused view (the "Use focused view" toggle state). */
	focusedViewEnabled: boolean;
	/** An admin has locked the rep into focused view, so there's no way back to the standard queue. */
	focusedViewLocked: boolean;
	showLiveTranscript: boolean;
	onShowLiveTranscriptChange: (enabled: boolean) => void;
	onRealtimeChange: (enabled: boolean) => void;
	onAutoSuggestionsChange: (enabled: boolean) => void;
	onReturnToQueue: () => void;
};

export const SettingsPanel: FC<SettingsPanelProps> = ({
	realtimeCoachingEnabled,
	autoSuggestionsEnabled,
	focusedViewEnabled,
	focusedViewLocked,
	showLiveTranscript,
	onShowLiveTranscriptChange,
	onRealtimeChange,
	onAutoSuggestionsChange,
	onReturnToQueue,
}) => {
	return (
		<div className={css(styles.root)}>
			<div className={css(styles.section)}>
				<h3 className={css(styles.title)}>Realtime Coaching</h3>
				<Toggle
					id='realtime-coaching-toggle'
					isOn={realtimeCoachingEnabled}
					text='Enable AI coaching during calls'
					onToggleCheckChanged={onRealtimeChange}
				/>
			</div>

			{realtimeCoachingEnabled && (
				<div className={css(styles.sectionDivider)}>
					<h3 className={css(styles.title)}>Auto Suggestions</h3>
					<Toggle
						id='auto-suggestions-toggle'
						isOn={autoSuggestionsEnabled}
						text='Automatically suggest during calls'
						onToggleCheckChanged={onAutoSuggestionsChange}
					/>
				</div>
			)}

			{realtimeCoachingEnabled && (
				<div className={css(styles.sectionDivider)}>
					<h3 className={css(styles.title)}>Live Transcript</h3>
					<Toggle
						id='live-transcript-toggle'
						isOn={showLiveTranscript}
						text='Show the transcript under live suggestions'
						onToggleCheckChanged={onShowLiveTranscriptChange}
					/>
				</div>
			)}

			{!focusedViewLocked && (
				<div className={css(styles.sectionDivider)}>
					<h3 className={css(styles.title)}>Focused View</h3>
					{/*
					 * Only rendered while focused view is on (always true for this panel,
					 * which lives inside the focused view). Gating the render avoids a dead
					 * "off" toggle with no way to turn focused view back on from here — the
					 * "Return to standard queue" link below is the unambiguous off action.
					 */}
					{focusedViewEnabled && (
						<Toggle
							id='focused-view-toggle'
							isOn={true}
							text='Use the new focused lead view'
							onToggleCheckChanged={next => {
								if (!next) onReturnToQueue();
							}}
						/>
					)}
					<Button variant='link' size='sm' styles={[styles.returnLink]} onClick={onReturnToQueue}>
						← Return to standard queue
					</Button>
				</div>
			)}
		</div>
	);
};

const styles = StyleSheet.create({
	root: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		// `--bg-elev` matches sibling drawer tabs (PinConfigPanel, Wrap-up) so all
		// tabs in the SideDrawer read as the same elevation surface.
		background: 'var(--bg-elev)',
		padding: 20,
		color: 'var(--ink)',
		overflowY: 'auto',
	},
	section: {
		paddingBottom: 4,
	},
	sectionDivider: {
		marginTop: 16,
		paddingTop: 16,
		borderTop: '1px solid var(--border)',
	},
	title: {
		fontSize: 14,
		fontWeight: 600,
		marginBottom: 10,
		marginTop: 0,
		color: 'var(--ink)',
	},
	returnLink: {
		padding: 0,
		marginTop: 12,
		justifyContent: 'flex-start',
	},
});
